/**
 * useLnVerificationRules — BACKLOG-0501 Task A.
 *
 * Fetches /v3/config/ln-verification-rules once per app session and caches
 * the result in module scope. Multiple components that mount within the
 * same session share the cache — the matrix is admin-edited rarely
 * (Bill 2026-05-19 spec defines 6 rows; edits happen quarterly at most),
 * so a per-session cache trades freshness for zero-cost reads on every
 * PersonModal open. A future cache-invalidation surface (e.g. a global
 * "refresh-config" action) can be wired by exporting the setter.
 *
 * Returns { rules, status, error } so callers can render a brief loading
 * state on first mount (the matrix usually returns in <100ms over the
 * Function key path).
 */

import { useEffect, useState } from 'react';
import type { LnVerificationRule } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

interface CacheState {
  status: LoadStatus;
  rules: ReadonlyArray<LnVerificationRule>;
  error: string | null;
  /** Pending fetch; subsequent callers await this same promise. */
  inflight: Promise<void> | null;
}

const cache: CacheState = {
  status: 'idle',
  rules: [],
  error: null,
  inflight: null,
};

// Per-subscriber notifier. useEffect-set state across multiple hook
// instances all kick once the shared promise resolves.
type Subscriber = () => void;
const subscribers = new Set<Subscriber>();
function notify(): void {
  for (const fn of subscribers) fn();
}

export interface LnRulesHook {
  readonly rules: ReadonlyArray<LnVerificationRule>;
  readonly status: LoadStatus;
  readonly error: string | null;
}

export function useLnVerificationRules(): LnRulesHook {
  const api = useApiClient();
  const [snapshot, setSnapshot] = useState<LnRulesHook>({
    rules: cache.rules,
    status: cache.status,
    error: cache.error,
  });

  useEffect(() => {
    const subscriber: Subscriber = () => {
      setSnapshot({ rules: cache.rules, status: cache.status, error: cache.error });
    };
    subscribers.add(subscriber);

    if (cache.status === 'idle') {
      cache.status = 'loading';
      cache.inflight = (async () => {
        try {
          const resp = await api.retail.config.lnVerificationRules();
          if (resp.success && Array.isArray(resp.data)) {
            cache.rules = resp.data;
            cache.status = 'ready';
            cache.error = null;
          } else {
            cache.status = 'error';
            cache.error = resp.error ?? 'failed to load LN verification rules';
          }
        } catch (e) {
          cache.status = 'error';
          cache.error = e instanceof Error ? e.message : String(e);
        } finally {
          cache.inflight = null;
          notify();
        }
      })();
      // Immediate notify so the caller flips from 'idle' → 'loading' state.
      notify();
    } else if (cache.status === 'loading' && cache.inflight) {
      // A previous mount kicked the fetch — wait for it.
      void cache.inflight.then(() => subscriber());
    }

    return () => {
      subscribers.delete(subscriber);
    };
  }, [api]);

  return snapshot;
}

/**
 * Test-only seam: reset the module-scope cache. Not exported via barrel.
 */
export function __resetLnRulesCacheForTests(): void {
  cache.status = 'idle';
  cache.rules = [];
  cache.error = null;
  cache.inflight = null;
}
